import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    minWidth: 140,
  },
  addForm: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr auto",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "end",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  phoneCell: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
  },
  nameCell: {
    fontWeight: 600,
  },
  nextRow: {
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
}));

const renderRotationChip = (status) => {
  switch (status) {
    case "next":
      return (
        <Chip
          size="small"
          color="primary"
          label={i18n.t("counterNumbers.rotation.next")}
        />
      );
    case "last":
      return (
        <Chip size="small" variant="outlined" label={i18n.t("counterNumbers.rotation.last")} />
      );
    case "inactive":
      return (
        <Chip size="small" label={i18n.t("counterNumbers.rotation.inactive")} />
      );
    default:
      return (
        <Chip
          size="small"
          variant="outlined"
          label={i18n.t("counterNumbers.rotation.queued")}
        />
      );
  }
};

const CounterNumbers = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({ id: "", name: "", message: "" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchNumbers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/counter-numbers", {
        params: { date: selectedDate },
      });
      setNumbers(data.numbers || []);
      setStats(data.stats || null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNumbers();
  }, [selectedDate]);

  const handleAdd = async () => {
    if (!form.phone.trim()) {
      toast.error(i18n.t("counterNumbers.form.phoneRequired"));
      return;
    }

    try {
      await api.post("/counter-numbers", form);
      toast.success(i18n.t("counterNumbers.toasts.added"));
      setForm({ name: "", phone: "", message: "" });
      fetchNumbers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/counter-numbers/${id}/toggle`);
      fetchNumbers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleEditOpen = (number) => {
    setEditData({
      id: number.id,
      name: number.name || "",
      message: number.message || "",
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/counter-numbers/${editData.id}`, {
        name: editData.name,
        message: editData.message,
      });
      toast.success(i18n.t("counterNumbers.toasts.updated"));
      setEditOpen(false);
      fetchNumbers();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/counter-numbers/${deleteId}`);
      toast.success(i18n.t("counterNumbers.toasts.deleted"));
      setDeleteOpen(false);
      setDeleteId(null);
      fetchNumbers();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("counterNumbers.confirmDelete.title")}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("counterNumbers.confirmDelete.message")}
      </ConfirmationModal>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("counterNumbers.edit.title")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={i18n.t("counterNumbers.form.name")}
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label={i18n.t("counterNumbers.form.message")}
            value={editData.message}
            onChange={(e) => setEditData({ ...editData, message: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>
            {i18n.t("counterNumbers.form.cancel")}
          </Button>
          <Button color="primary" variant="contained" onClick={handleEditSave}>
            {i18n.t("counterNumbers.form.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>{i18n.t("counterNumbers.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <IconButton onClick={fetchNumbers}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.statsRow}>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("counterNumbers.stats.daily")}
          </Typography>
          <Typography variant="h6">{stats?.daily_clicks ?? 0}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("counterNumbers.stats.total")}
          </Typography>
          <Typography variant="h6">{stats?.total_clicks ?? 0}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">
            {i18n.t("counterNumbers.stats.active")}
          </Typography>
          <Typography variant="h6">{stats?.active_count ?? 0}</Typography>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="h6" gutterBottom>
          {i18n.t("counterNumbers.form.addTitle")}
        </Typography>
        <div className={classes.addForm}>
          <TextField
            label={i18n.t("counterNumbers.form.name")}
            placeholder={i18n.t("counterNumbers.form.namePlaceholder")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            required
            label={i18n.t("counterNumbers.form.phone")}
            placeholder="351..."
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <TextField
            label={i18n.t("counterNumbers.form.message")}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <Button variant="contained" color="primary" onClick={handleAdd}>
            {i18n.t("counterNumbers.form.add")}
          </Button>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("counterNumbers.table.active")}</TableCell>
              <TableCell align="center">{i18n.t("counterNumbers.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("counterNumbers.table.daily")}</TableCell>
              <TableCell align="center">{i18n.t("counterNumbers.table.total")}</TableCell>
              <TableCell align="center">{i18n.t("counterNumbers.table.rotation")}</TableCell>
              <TableCell align="center">{i18n.t("counterNumbers.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {numbers.map((number) => (
              <TableRow
                key={number.id}
                className={number.rotation_status === "next" ? classes.nextRow : ""}
              >
                <TableCell align="center">
                  <Switch
                    checked={number.active}
                    onChange={() => handleToggle(number.id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="left">
                  <div className={classes.nameCell}>{number.name}</div>
                  <div className={classes.phoneCell}>{number.phone}</div>
                  {number.message && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {number.message}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">{number.daily_clicks}</TableCell>
                <TableCell align="center">{number.clicks}</TableCell>
                <TableCell align="center">
                  {renderRotationChip(number.rotation_status)}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEditOpen(number)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeleteId(number.id);
                      setDeleteOpen(true);
                    }}
                    disabled={numbers.length <= 1}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default CounterNumbers;
